import React, { useMemo, useState } from "react";
import { FiPlus, FiX } from "react-icons/fi";

type HashtagInputProps = {
	value: string[];
	onChange: (tags: string[]) => void;
	max?: number;
	label?: string;
	description?: string;
	placeholder?: string;
	className?: string;
	compact?: boolean;
};

const normalizeTag = (text: string) =>
	text
		.replace(/^[#＃]+/, "")
		.trim()
		.replace(/[\s\u3000]+/g, "");

const HashtagInput: React.FC<HashtagInputProps> = ({
	value,
	onChange,
	max = 10,
	label = "ハッシュタグ",
	description = "SNS検索用に最大10個まで設定できます（#は省略可）",
	placeholder = "例）ロック、フェス、学園祭",
	className = "",
	compact = false,
}) => {
	const [inputValue, setInputValue] = useState("");
	const [error, setError] = useState<string | null>(null);

	const remaining = useMemo(
		() => Math.max(0, max - value.length),
		[max, value]
	);

	const addTag = () => {
		const tag = normalizeTag(inputValue);
		if (!tag) {
			setError(null);
			return;
		}

		const exists = value.some(
			(existing) => existing.toLowerCase() === tag.toLowerCase()
		);
		if (exists) {
			setError("同じハッシュタグが既に追加されています");
			return;
		}

		if (value.length >= max) {
			setError(`ハッシュタグは最大${max}個までです`);
			return;
		}

		onChange([...value, tag]);
		setInputValue("");
		setError(null);
	};

	const removeTag = (tag: string) => {
		onChange(value.filter((t) => t !== tag));
		setError(null);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter" || e.key === "Tab" || e.key === ",") {
			e.preventDefault();
			addTag();
		}
	};

	const inputClasses = compact
		? "px-3 py-2 text-sm"
		: "px-3 sm:px-4 py-2 text-base";
	const pillClasses = compact ? "text-xs px-2 py-1" : "text-sm px-3 py-1.5";
	const addButtonBase =
		"inline-flex items-center gap-1 rounded-lg font-medium text-white transition-all shadow-sm hover:shadow-md";
	const addButtonSizes = compact
		? "px-3 py-2 text-sm"
		: "px-3 sm:px-4 py-2.5 text-sm";
	const addButtonColors =
		"bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700";

	return (
		<div className={`space-y-3 ${className}`}>
			<div className="flex items-baseline justify-between">
				<div>
					<p className="text-sm font-medium text-gray-700 dark:text-gray-200">
						{label}
					</p>
					<p className="text-xs text-gray-500 dark:text-gray-400">
						{description}
					</p>
				</div>
				<p className="text-xs text-gray-500 dark:text-gray-400">
					残り {remaining} 個
				</p>
			</div>
			<div className="flex items-center gap-2">
				<input
					type="text"
					value={inputValue}
					onChange={(e) => setInputValue(e.target.value)}
					onKeyDown={handleKeyDown}
					placeholder={placeholder}
					className={`flex-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 transition-colors ${inputClasses}`}
				/>
				<button
					type="button"
					onClick={addTag}
					className={`${addButtonBase} ${addButtonSizes} ${addButtonColors}`}
				>
					<FiPlus />
					追加
				</button>
			</div>
			{error && (
				<p className="text-xs text-red-500 dark:text-red-400">{error}</p>
			)}
			<div className="flex flex-wrap gap-2">
				{value.length === 0 && (
					<span className="text-xs text-gray-500 dark:text-gray-400">
						まだハッシュタグがありません
					</span>
				)}
				{value.map((tag) => (
					<span
						key={tag}
						className={`inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 ${pillClasses}`}
					>
						<span className="text-violet-600 dark:text-violet-300">#</span>
						<span>{tag}</span>
						<button
							type="button"
							aria-label={`${tag}を削除`}
							onClick={() => removeTag(tag)}
							className="hover:text-red-500 dark:hover:text-red-400"
						>
							<FiX className={compact ? "h-3 w-3" : "h-4 w-4"} />
						</button>
					</span>
				))}
			</div>
		</div>
	);
};

export default HashtagInput;
